// Meta Pixel with regional advertising-consent gating.
// Consent regions (EEA, UK, CH, and unknown) need an explicit Accept before the
// pixel loads. Elsewhere it loads immediately.
const PIXEL_ID = "1841162446884229";
export const NOTICE_VERSION = "2026-09-26";
const STORE_KEY = "ffa-ad-consent";
const CONSENT_COUNTRIES = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT","LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE","IS","LI","NO","GB","CH",
]);

export type ConsentChoice = "granted" | "denied";
export interface ConsentRecord { choice: ConsentChoice; decidedAt: string; noticeVersion: string; region: string }

type Fbq = ((...args: unknown[]) => void) & { callMethod?: any; queue?: unknown[]; loaded?: boolean; version?: string; push?: any };
declare global { interface Window { fbq?: Fbq; _fbq?: Fbq } }

let regionPromise: Promise<{ country: string; requiresConsent: boolean }> | null = null;
let loaded = false;
const listeners = new Set<() => void>();

export function getRegion() {
  if (!regionPromise) {
    regionPromise = (async () => {
      try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 2000);
        const res = await fetch("/cdn-cgi/trace", { signal: ctrl.signal, cache: "no-store" });
        clearTimeout(t);
        if (!res.ok) throw new Error("trace");
        const country = (/^loc=(\w+)$/m.exec(await res.text())?.[1] ?? "XX").toUpperCase();
        const requiresConsent = country === "XX" || country === "T1" || CONSENT_COUNTRIES.has(country);
        return { country, requiresConsent };
      } catch {
        return { country: "XX", requiresConsent: true };
      }
    })();
  }
  return regionPromise;
}

export function readConsent(): ConsentRecord | null {
  try { const r = localStorage.getItem(STORE_KEY); return r ? JSON.parse(r) : null; } catch { return null; }
}

export function saveConsent(choice: ConsentChoice, region: string) {
  const rec: ConsentRecord = { choice, decidedAt: new Date().toISOString(), noticeVersion: NOTICE_VERSION, region };
  try { localStorage.setItem(STORE_KEY, JSON.stringify(rec)); } catch { /* */ }
  void applyConsent();
  listeners.forEach((l) => l());
}

export function onConsentUIRequest(l: () => void) { listeners.add(l); return () => listeners.delete(l); }
const openers = new Set<() => void>();
export function registerCookieSettingsOpener(fn: () => void) { openers.add(fn); return () => openers.delete(fn); }
export function openCookieSettings() { openers.forEach((f) => f()); }

/** True when ad tracking may run right now (read fresh each call). */
export async function trackingAllowed(): Promise<boolean> {
  const c = readConsent();
  if (c?.choice === "denied") return false;
  const { requiresConsent } = await getRegion();
  return !requiresConsent || c?.choice === "granted";
}

function loadScript() {
  if (loaded || typeof window === "undefined") return;
  loaded = true;
  /* eslint-disable */
  (function (f: any, b: Document, e: string, v: string) {
    if (f.fbq) return;
    const n: any = (f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); });
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement; t.async = true; t.src = v;
    const s = b.getElementsByTagName(e)[0]; s.parentNode!.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq!("init", PIXEL_ID);
}

export async function applyConsent() {
  if (await trackingAllowed()) {
    loadScript();
    window.fbq?.("consent", "grant");
  } else if (window.fbq) {
    window.fbq("consent", "revoke");
  }
}

export async function trackPageView() {
  if (!(await trackingAllowed())) return;
  loadScript();
  window.fbq?.("track", "PageView");
}

export async function trackEvent(name: string, params?: Record<string, unknown>, opts?: { custom?: boolean; eventId?: string }) {
  try {
    if (!(await trackingAllowed())) return;
    loadScript();
    window.fbq?.(opts?.custom ? "trackCustom" : "track", name, params ?? {}, opts?.eventId ? { eventID: opts.eventId } : undefined);
  } catch { /* never break the app */ }
}

function cookie(name: string) {
  return document.cookie.split("; ").find((c) => c.startsWith(name + "="))?.split("=")[1] ?? null;
}

/** Browser IDs + consent record, sent to the server for Purchase attribution. */
export async function getSignalPayload() {
  const { country, requiresConsent } = await getRegion();
  const c = readConsent();
  const consent = c?.choice === "denied" ? "denied" : requiresConsent ? (c?.choice ?? "denied") : "not_required";
  let fbc = cookie("_fbc");
  const fbclid = new URLSearchParams(location.search).get("fbclid");
  if (!fbc && fbclid) fbc = `fb.1.${Date.now()}.${fbclid}`;
  return {
    consent, region: requiresConsent ? country : null,
    noticeVersion: c?.noticeVersion ?? null, decidedAt: c?.decidedAt ?? null,
    fbp: consent === "denied" ? null : cookie("_fbp"), fbc: consent === "denied" ? null : fbc,
  };
}

// Apply other tabs' consent changes immediately.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => { if (e.key === STORE_KEY) { void applyConsent(); listeners.forEach((l) => l()); } });
}

// ---- CompleteRegistration (fired at onboarding save; once per user) ----
const REG_SENT = (id: string) => `ffa-meta-reg-sent-${id}`;
const REG_PENDING = (id: string) => `ffa-meta-reg-pending-${id}`;
const REG_WINDOW_MS = 30 * 60 * 1000;

/**
 * Fire standard CompleteRegistration once per user. The "sent" marker is only
 * written after tracking is allowed and fbq has queued the event. If tracking is
 * not yet allowed, a timestamped pending marker is saved so it can be sent if
 * consent is granted shortly after (see retryPendingRegistration).
 */
export async function trackCompleteRegistrationOnce(userId: string): Promise<boolean> {
  try {
    if (!userId || localStorage.getItem(REG_SENT(userId))) return false;
    if (!(await trackingAllowed())) {
      if (readConsent()?.choice !== "denied" && !localStorage.getItem(REG_PENDING(userId))) {
        localStorage.setItem(REG_PENDING(userId), String(Date.now()));
      }
      return false;
    }
    if (localStorage.getItem(REG_SENT(userId))) return false;
    loadScript();
    if (!window.fbq) return false;
    window.fbq("track", "CompleteRegistration", {}, { eventID: `reg_${userId}` });
    localStorage.setItem(REG_SENT(userId), String(Date.now()));
    localStorage.removeItem(REG_PENDING(userId));
    return true;
  } catch { return false; }
}

/** Send a pending CompleteRegistration if consent was granted within 30 min of onboarding. */
export async function retryPendingRegistration(userId: string) {
  try {
    const p = Number(localStorage.getItem(REG_PENDING(userId)) || 0);
    if (!p) return;
    if (Date.now() - p > REG_WINDOW_MS || readConsent()?.choice === "denied") {
      localStorage.removeItem(REG_PENDING(userId));
      return;
    }
    await trackCompleteRegistrationOnce(userId);
  } catch { /* non-critical */ }
}
